import React, { useState } from 'react';
import InputBase from '@mui/material/InputBase';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import commons from '../../../assets/styles/commons';
import colors from '../../../assets/styles/colors';
import { Control, Controller, UseControllerProps } from 'react-hook-form';
import { FormHelperText, styled } from '@material-ui/core';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { VisibilityOffOutlined, VisibilityOutlined } from '@material-ui/icons';

interface FormInputProps extends Omit<UseControllerProps, 'defaultValue'> {
  label?: string;
  name: string;
  id?: string;
  style?: React.CSSProperties;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: number | string;
  defaultValue?: number | string;
  placeholder?: string;
  mask?: string;
  control?: Control<any>;
  errorMessage?: string;
  size?: 'small' | 'medium';
  startAdornment?: any;
  endAdornment?: any;
  onBlur?: () => void;
  maxLength?: number;
  type?: string;
  labelColor?: string;
  className?: string;
}

const CustomInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
    color: colors.neutral.gray,
  },
  '&.MuiInputBase-root': {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: colors.secondary.softBlue,
    border: '1px solid',
    borderColor: colors.primary.orange,
    flex: 1,
    width: 'auto',
    height: '18px',
    padding: '7px 12px',
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    fontFamily: commons.fontFamily,
    fontVariant: 'lining-nums',
    '&:focus': {
      boxShadow: `rgba(255, 114, 6, 0.25) 0 0 0 0.2rem`,
      borderColor: colors.primary.orange,
    },
    '&.Mui-focused': {
      color: colors.primary.orange,
    },
  },

  '& .MuiInputBase-input': {
    padding: '0',
  },

  '& .MuiTypography-root': {
    fontFamily: commons.fontFamily,
    color: colors.primary.orange,
    fontSize: 18,
  },
  '& .MuiInputAdornment-root': {
    marginLeft: 0,
  },
  '& .MuiInputAdornment-positionStart': {
    marginRight: 0,
  },
}));

const PasswordInput: React.FC<FormInputProps> = ({
  label,
  id,
  style,
  placeholder,
  name,
  control,
  startAdornment,
  defaultValue,
  maxLength,
  size = 'small',
  errorMessage,
  labelColor = 'orange',
  className,
}) => {
  const getLabelColor = () => {
    if (labelColor === 'white') {
      return colors.base;
    }
    return colors.primary.orange;
  };

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  return (
    <Box component="div" sx={{ display: 'grid' }}>
      <Controller
        name={name || ''}
        control={control}
        render={({
          field: { ref, onBlur, onChange, value },
          fieldState: { error },
        }) => (
          <FormControl size={size}>
            <InputLabel
              variant="standard"
              shrink
              htmlFor={id}
              sx={{
                fontFamily: commons.fontFamily,
                color: getLabelColor(),
                fontSize: 18,
                '&.Mui-focused': {
                  color: getLabelColor(),
                },
                ...style,
              }}
            >
              {label}
            </InputLabel>
            <CustomInput
              value={value}
              defaultValue={defaultValue}
              onChange={onChange}
              id={id}
              placeholder={placeholder}
              ref={ref}
              onBlur={onBlur}
              type={showPassword ? 'text' : 'password'}
              inputProps={{ maxLength }}
              error={!!error?.message}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={togglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOffOutlined />
                    ) : (
                      <VisibilityOutlined />
                    )}
                  </IconButton>
                </InputAdornment>
              }
              startAdornment={
                <InputAdornment position="start">
                  {startAdornment}
                </InputAdornment>
              }
            />
            {!!error && (
              <FormHelperText style={{ color: colors.error.intenseRed }}>
                {error?.message || errorMessage}
              </FormHelperText>
            )}
          </FormControl>
        )}
      />
    </Box>
  );
};

export default PasswordInput;
